import React, { useState, useEffect } from 'react';
import { Anchor, Plus, RefreshCw, Download, AlertTriangle, CheckCircle, XCircle, Clock, Star, Ship, Trash2, RotateCcw, Undo2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const Anchorage = () => {
  const [anchorages, setAnchorages] = useState([]);
  const [deletedAnchorages, setDeletedAnchorages] = useState([]);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [vessels, setVessels] = useState([]); // Kayıtlı gemiler
  const [familiarShips, setFamiliarShips] = useState([]); // Tanıdık armatör gemileri
  const [settings, setSettings] = useState(null); // Tarife ayarları
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedAnchorage, setSelectedAnchorage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [exitFormData, setExitFormData] = useState({
    exit_date: new Date().toISOString().split('T')[0],
    exit_time: new Date().toTimeString().slice(0, 5),
    notes: ''
  });
  
  // Pagination states
  const [currentPageCritical, setCurrentPageCritical] = useState(1);
  const [currentPageNormal, setCurrentPageNormal] = useState(1);
  const [currentPageDeparted, setCurrentPageDeparted] = useState(1);
  const [currentPageDeleted, setCurrentPageDeleted] = useState(1);
  const itemsPerPage = 50;
  
  // Selection states for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
    loadVessels();
    loadFamiliarShips();
    loadSettings();
    
    // Auto-refresh every 60 seconds
    const intervalId = setInterval(() => {
      loadData();
    }, 60000);

    return () => clearInterval(intervalId);
  }, [filterStatus]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Ayarlar yüklenemedi');
    }
  };

  const loadVessels = async () => {
    try {
      const response = await api.get('/vessels');
      setVessels(response.data);
    } catch (error) {
      console.error('Gemiler yüklenemedi');
    }
  };

  const loadFamiliarShips = async () => {
    try {
      const response = await api.get('/familiar-ships');
      setFamiliarShips(response.data);
    } catch (error) {
      console.error('Tanıdık gemiler yüklenemedi');
    }
  };

  // Kayıtlı gemi kontrolü
  const isRegisteredVessel = (vesselName) => {
    if (!vesselName || !vessels.length) return false;
    const normalizedName = vesselName.toLowerCase().trim();
    return vessels.some(v => v.name.toLowerCase().trim() === normalizedName);
  };

  // Kayıtlı gemi bilgilerini getir
  const getRegisteredVessel = (vesselName) => {
    if (!vesselName || !vessels.length) return null;
    const normalizedName = vesselName.toLowerCase().trim();
    return vessels.find(v => v.name.toLowerCase().trim() === normalizedName);
  };

  // Demirleme ücreti hesapla (TBDTDY Hariç) - Settings'den oranları al
  const calculateAnchoringFee = (vesselName, hoursAnchored) => {
    const vessel = getRegisteredVessel(vesselName);
    if (!vessel || !vessel.gross_tonnage) return null;
    
    const gt = parseFloat(vessel.gross_tonnage);
    const days = Math.ceil(hoursAnchored / 24); // Gün sayısı (yukarı yuvarla)
    
    // Ücretsiz gün sayısı - Settings'den al veya varsayılan 3 gün
    const freeDays = settings?.anchoring_free_days ?? 3;
    
    // Ücretsiz süre dolmadıysa ücret gösterme
    if (days <= freeDays) {
      return null;
    }
    
    // Bayrak kontrolü
    const flag = (vessel.flag || '').toLowerCase();
    const isTurkish = flag.includes('turk') || flag.includes('turkey') || flag.includes('türk');
    
    // Tarifeler - Settings'den al veya varsayılan kullan (TBDTDY Hariç)
    let rateUnder168h, rateOver168h;
    if (isTurkish) {
      rateUnder168h = settings?.anchoring_rate_turkish_under_168h || 0.002;
      rateOver168h = settings?.anchoring_rate_turkish_over_168h || 0.003;
    } else {
      rateUnder168h = settings?.anchoring_rate_foreign_under_168h || 0.004;
      rateOver168h = settings?.anchoring_rate_foreign_over_168h || 0.006;
    }
    
    // Ücretsiz süre geçildiğinde İLK GÜNDEN itibaren ücret hesapla
    let totalFeeUsd;
    if (days <= 7) {
      // Tamamı ≤168h tarifesinden
      totalFeeUsd = days * gt * rateUnder168h;
    } else {
      // İlk 7 gün ≤168h, kalan günler >168h
      const feeFirst7 = 7 * gt * rateUnder168h;
      const feeRemaining = (days - 7) * gt * rateOver168h;
      totalFeeUsd = feeFirst7 + feeRemaining;
    }
    
    return {
      fee: totalFeeUsd,
      days: days,
      gt: gt,
      isTurkish: isTurkish,
      rateUnder: rateUnder168h,
      rateOver: rateOver168h,
      freeDays: freeDays
    };
  };

  // Tanıdık armatör kontrolü
  const isFamiliarShip = (vesselName) => {
    if (!vesselName || !familiarShips.length) return false;
    const normalizedName = vesselName.toLowerCase().trim();
    return familiarShips.some(s => s.ship_name.toLowerCase().trim() === normalizedName);
  };

  // Tanıdık armatör toggle
  const toggleFamiliarShip = async (anchorage) => {
    try {
      const response = await api.post('/familiar-ships/toggle', {
        ship_name: anchorage.ship_name,
        mmsi: anchorage.mmsi
      });
      
      if (response.data.action === 'added') {
        toast.success(`${anchorage.ship_name} tanıdık listesine eklendi`);
      } else {
        toast.success(`${anchorage.ship_name} tanıdık listesinden çıkarıldı`);
      }
      
      loadFamiliarShips();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const loadData = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/anchorage${params}`);
      setAnchorages(response.data);
    } catch (error) {
      if (loading) {
        toast.error('Veriler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  // Silinmiş kayıtları yükle
  const loadDeletedAnchorages = async () => {
    try {
      const response = await api.get('/anchorage/deleted/list');
      setDeletedAnchorages(response.data);
    } catch (error) {
      toast.error('Silinmiş kayıtlar yüklenemedi');
    }
  };

  // Silinmiş kaydı geri yükle
  const handleRestoreAnchorage = async (anchorageId) => {
    try {
      await api.put(`/anchorage/${anchorageId}/restore`);
      toast.success('Kayıt başarıyla geri yüklendi');
      loadDeletedAnchorages();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri yükleme başarısız');
    }
  };

  // Çıkışı iptal et
  const handleCancelExit = async (anchorage) => {
    if (!window.confirm(`${anchorage.ship_name} gemisinin çıkışını iptal etmek istediğinizden emin misiniz? Gemi tekrar demirsahasına eklenecek.`)) {
      return;
    }
    
    try {
      await api.put(`/anchorage/${anchorage.id}/cancel-exit`);
      toast.success('Çıkış iptal edildi, gemi tekrar demirsahasına eklendi');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Çıkış iptal edilemedi');
    }
  };

  // Silinmiş kayıtlar modalını aç
  const openDeletedModal = async () => {
    await loadDeletedAnchorages();
    setShowDeletedModal(true);
  };

  const syncEmails = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/anchorage/sync-emails');
      const { entries_created, exits_updated, skipped, errors } = response.data;
      
      let message = 'Email senkronizasyonu tamamlandı: ';
      const parts = [];
      
      if (entries_created > 0) parts.push(`${entries_created} giriş`);
      if (exits_updated > 0) parts.push(`${exits_updated} çıkış`);
      if (skipped > 0) parts.push(`${skipped} atlandı`);
      if (errors > 0) parts.push(`${errors} hata`);
      
      message += parts.join(', ');
      
      toast.success(message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email senkronizasyonu başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const handleExitClick = (anchorage) => {
    setSelectedAnchorage(anchorage);
    setShowExitModal(true);
  };

  const handleExitSubmit = async (e) => {
    e.preventDefault();
    try {
      const exitDateTime = new Date(`${exitFormData.exit_date}T${exitFormData.exit_time}:00Z`);
      
      await api.put(`/anchorage/${selectedAnchorage.id}/exit`, {
        exit_date: exitDateTime.toISOString(),
        notes: exitFormData.notes || null
      });
      
      toast.success('Gemi çıkışı başarıyla kaydedildi');
      setShowExitModal(false);
      resetExitForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Çıkış kaydedilemedi');
    }
  };

  const resetExitForm = () => {
    setExitFormData({
      exit_date: new Date().toISOString().split('T')[0],
      exit_time: new Date().toTimeString().slice(0, 5),
      notes: ''
    });
    setSelectedAnchorage(null);
  };

  const handleSelectAll = (anchorageList) => {
    const allIds = anchorageList.map(anc => anc.id);
    if (allIds.every(id => selectedIds.includes(id))) {
      // If all are selected, deselect all
      setSelectedIds(selectedIds.filter(id => !allIds.includes(id)));
    } else {
      // Select all
      setSelectedIds([...new Set([...selectedIds, ...allIds])]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('Lütfen silmek için en az bir gemi seçin');
      return;
    }

    const confirmed = window.confirm(
      `${selectedIds.length} gemi kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`
    );

    if (!confirmed) return;

    setDeleting(true);
    try {
      const deletePromises = selectedIds.map(id => 
        api.delete(`/anchorage/${id}`)
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`${selectedIds.length} gemi kaydı başarıyla silindi`);
      setSelectedIds([]);
      loadData();
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  const formatDuration = (hours) => {
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    const minutes = Math.floor((hours % 1) * 60);
    
    return `${days}g ${remainingHours}s ${minutes}d`;
  };

  // Takvim günü hesapla (geliş tarihinden bugüne veya çıkış tarihine)
  const calculateCalendarDays = (entryDate, exitDate = null) => {
    const entry = new Date(entryDate);
    const end = exitDate ? new Date(exitDate) : new Date();
    
    // Gün farkını hesapla
    const diffTime = end.getTime() - entry.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(1, diffDays); // En az 1 gün
  };

  const getAlertBadge = (alertLevel, hoursAnchored) => {
    const hours168 = 168;
    const hours156 = 156;
    
    if (hoursAnchored >= hours168) {
      return { class: 'badge-danger', text: 'Süre Geçti', icon: XCircle };
    } else if (hoursAnchored >= hours156) {
      return { class: 'badge-warning', text: 'Kritik', icon: AlertTriangle };
    } else {
      return { class: 'badge-success', text: 'Normal', icon: CheckCircle };
    }
  };

  const getStatusBadge = (status) => {
    return status === 'anchored' 
      ? { class: 'badge-primary', text: 'Demirsahasında' }
      : { class: 'badge-secondary', text: 'Çıkış Yaptı' };
  };

  const exportToExcel = () => {
    const filteredData = getFilteredAnchorages();
    
    // Create CSV content
    const headers = ['Gemi Adı', 'MMSI', 'Geliş Tarihi', 'Çıkış Tarihi', 'Süre', 'Durum', 'Uyarı'];
    const rows = filteredData.map(anc => [
      anc.ship_name,
      anc.mmsi || '-',
      new Date(anc.entry_date).toLocaleString('tr-TR'),
      anc.exit_date ? new Date(anc.exit_date).toLocaleString('tr-TR') : '-',
      formatDuration(anc.hours_anchored),
      anc.status === 'anchored' ? 'Demirsahasında' : 'Çıkış Yaptı',
      getAlertBadge(anc.alert_level, anc.hours_anchored).text
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `demirsahasi_raporu_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Rapor indirildi');
  };

  const getFilteredAnchorages = () => {
    return anchorages.filter(anc => {
      const matchesSearch = anc.ship_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (anc.mmsi && anc.mmsi.includes(searchTerm));
      return matchesSearch;
    });
  };

  const filteredAnchorages = getFilteredAnchorages();

  // Separate into categories
  const criticalAndExceeded = filteredAnchorages.filter(anc => 
    anc.status === 'anchored' && anc.hours_anchored >= 156
  );
  const activeNormal = filteredAnchorages.filter(anc => 
    anc.status === 'anchored' && anc.hours_anchored < 156
  );
  const departed = filteredAnchorages.filter(anc => anc.status === 'departed');

  // Pagination logic
  const getPaginatedData = (data, currentPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (dataLength) => {
    return Math.ceil(dataLength / itemsPerPage);
  };

  const renderPagination = (totalItems, currentPage, setCurrentPage) => {
    const totalPages = getTotalPages(totalItems);
    if (totalPages <= 1) return null;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          Önceki
        </button>
        <span style={{ padding: '0 1rem' }}>
          Sayfa {currentPage} / {totalPages} ({totalItems} kayıt)
        </span>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          Sonraki
        </button>
      </div>
    );
  };

  // Get paginated data for each section
  const paginatedCritical = getPaginatedData(criticalAndExceeded, currentPageCritical);
  const paginatedNormal = getPaginatedData(activeNormal, currentPageNormal);
  const paginatedDeparted = getPaginatedData(departed, currentPageDeparted);

  if (loading) {
    return (
      <div className="content">
        <div className="topbar">
          <h1 className="topbar-title">Demirsahası Takip</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title">
          <Anchor size={24} style={{ marginRight: '0.5rem' }} />
          Sinop Demirsahası Takip
        </h1>
        <div className="topbar-actions">
          {selectedIds.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              <XCircle size={18} />
              {deleting ? 'Siliniyor...' : `Seçilileri Sil (${selectedIds.length})`}
            </button>
          )}
          <button 
            className="btn" 
            onClick={openDeletedModal}
            style={{ backgroundColor: '#6b7280', color: 'white' }}
            title="Silinmiş kayıtları görüntüle ve geri yükle"
          >
            <Trash2 size={18} />
            Silinmiş Kayıtlar
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={exportToExcel}
            disabled={filteredAnchorages.length === 0}
          >
            <Download size={18} />
            Excel İndir
          </button>
          <button 
            className="btn btn-primary" 
            onClick={syncEmails}
            disabled={syncing}
          >
            <RefreshCw size={18} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Senkronize Ediliyor...' : 'Email Senkronize Et'}
          </button>
        </div>
      </div>

      <div style={{ padding: '2rem' }}>
        {/* Filters and Search */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-body">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Gemi adı veya MMSI ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className={`btn ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterStatus('all')}
                >
                  Tümü ({anchorages.length})
                </button>
                <button
                  className={`btn ${filterStatus === 'anchored' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterStatus('anchored')}
                >
                  Demirsahasında ({anchorages.filter(a => a.status === 'anchored').length})
                </button>
                <button
                  className={`btn ${filterStatus === 'departed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterStatus('departed')}
                >
                  Çıkış Yaptı ({anchorages.filter(a => a.status === 'departed').length})
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Critical and Exceeded Section */}
        {criticalAndExceeded.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #dc3545' }}>
            <div className="card-header" style={{ backgroundColor: '#fff5f5', padding: '0.75rem 1rem' }}>
              <h3 className="card-title" style={{ color: '#dc3545', fontSize: '1.1rem', margin: 0 }}>
                <AlertTriangle size={18} style={{ marginRight: '0.5rem' }} />
                Kritik ve Süre Geçen Gemiler ({criticalAndExceeded.length})
              </h3>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-responsive">
                <table className="table table-sm" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr style={{ fontSize: '0.85rem' }}>
                      <th style={{ padding: '0.5rem', width: '40px' }}>
                        <input
                          type="checkbox"
                          onChange={() => handleSelectAll(paginatedCritical)}
                          checked={paginatedCritical.length > 0 && paginatedCritical.every(anc => selectedIds.includes(anc.id))}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ padding: '0.5rem' }}>Gemi Adı</th>
                      <th style={{ padding: '0.5rem' }}>MMSI</th>
                      <th style={{ padding: '0.5rem' }}>Geliş Tarihi & Saati</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Demir Ücreti</th>
                      <th style={{ padding: '0.5rem' }}>Bekleme Süresi</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Gün</th>
                      <th style={{ padding: '0.5rem' }}>Uyarı</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCritical.map((anchorage) => {
                      const badge = getAlertBadge(anchorage.alert_level, anchorage.hours_anchored);
                      const BadgeIcon = badge.icon;
                      const isRegistered = isRegisteredVessel(anchorage.ship_name);
                      const isFamiliar = isFamiliarShip(anchorage.ship_name);
                      const anchoringFee = calculateAnchoringFee(anchorage.ship_name, anchorage.hours_anchored);
                      
                      return (
                        <tr 
                          key={anchorage.id} 
                          style={{ 
                            fontSize: '0.9rem',
                            backgroundColor: isRegistered ? '#e8f5e9' : isFamiliar ? '#fff3e0' : 'inherit'
                          }}
                        >
                          <td style={{ padding: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(anchorage.id)}
                              onChange={() => handleSelectOne(anchorage.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <strong>{anchorage.ship_name}</strong>
                              {isRegistered && (
                                <span className="badge badge-success" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }} title="Kayıtlı Gemi">
                                  <Ship size={10} /> Kayıtlı
                                </span>
                              )}
                              {isFamiliar && !isRegistered && (
                                <span className="badge" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', backgroundColor: '#ff9800', color: 'white' }} title="Tanıdık Armatör">
                                  <Star size={10} /> Tanıdık
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '0.5rem' }}>{anchorage.mmsi || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{new Date(anchorage.entry_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            {anchoringFee ? (
                              <span style={{ fontWeight: 600, color: '#059669' }} title={`${anchoringFee.gt} GT, ${anchoringFee.days} gün, ${anchoringFee.isTurkish ? 'Türk' : 'Yabancı'} Bayrak`}>
                                ${anchoringFee.fee.toFixed(2)}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <span style={{ fontWeight: 'bold' }}>
                              {formatDuration(anchorage.hours_anchored)}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#6366f1' }}>
                              {calculateCalendarDays(anchorage.entry_date)}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <span className={`badge ${badge.class}`} style={{ fontSize: '0.8rem' }}>
                              <BadgeIcon size={12} style={{ marginRight: '0.25rem' }} />
                              {badge.text}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                              <button
                                className={`btn btn-sm ${isFamiliar ? 'btn-warning' : 'btn-outline-warning'}`}
                                onClick={() => toggleFamiliarShip(anchorage)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                title={isFamiliar ? 'Tanıdık listesinden çıkar' : 'Tanıdık listesine ekle'}
                              >
                                <Star size={14} fill={isFamiliar ? 'currentColor' : 'none'} />
                              </button>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleExitClick(anchorage)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                              >
                                <CheckCircle size={14} />
                                Çıkış
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {renderPagination(criticalAndExceeded.length, currentPageCritical, setCurrentPageCritical)}
            </div>
          </div>
        )}

        {/* Active Normal Section */}
        {activeNormal.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header" style={{ padding: '0.75rem 1rem' }}>
              <h3 className="card-title" style={{ fontSize: '1.1rem', margin: 0 }}>
                <Clock size={18} style={{ marginRight: '0.5rem' }} />
                Demirsahasındaki Gemiler ({activeNormal.length})
              </h3>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-responsive">
                <table className="table table-sm" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr style={{ fontSize: '0.85rem' }}>
                      <th style={{ padding: '0.5rem', width: '40px' }}>
                        <input
                          type="checkbox"
                          onChange={() => handleSelectAll(paginatedNormal)}
                          checked={paginatedNormal.length > 0 && paginatedNormal.every(anc => selectedIds.includes(anc.id))}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ padding: '0.5rem' }}>Gemi Adı</th>
                      <th style={{ padding: '0.5rem' }}>MMSI</th>
                      <th style={{ padding: '0.5rem' }}>Geliş Tarihi & Saati</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Demir Ücreti</th>
                      <th style={{ padding: '0.5rem' }}>Bekleme Süresi</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Gün</th>
                      <th style={{ padding: '0.5rem' }}>Uyarı</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedNormal.map((anchorage) => {
                      const badge = getAlertBadge(anchorage.alert_level, anchorage.hours_anchored);
                      const BadgeIcon = badge.icon;
                      const isRegistered = isRegisteredVessel(anchorage.ship_name);
                      const isFamiliar = isFamiliarShip(anchorage.ship_name);
                      const anchoringFee = calculateAnchoringFee(anchorage.ship_name, anchorage.hours_anchored);
                      
                      return (
                        <tr 
                          key={anchorage.id} 
                          style={{ 
                            fontSize: '0.9rem',
                            backgroundColor: isRegistered ? '#e8f5e9' : isFamiliar ? '#fff3e0' : 'inherit'
                          }}
                        >
                          <td style={{ padding: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(anchorage.id)}
                              onChange={() => handleSelectOne(anchorage.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <strong>{anchorage.ship_name}</strong>
                              {isRegistered && (
                                <span className="badge badge-success" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }} title="Kayıtlı Gemi">
                                  <Ship size={10} /> Kayıtlı
                                </span>
                              )}
                              {isFamiliar && !isRegistered && (
                                <span className="badge" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', backgroundColor: '#ff9800', color: 'white' }} title="Tanıdık Armatör">
                                  <Star size={10} /> Tanıdık
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '0.5rem' }}>{anchorage.mmsi || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{new Date(anchorage.entry_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            {anchoringFee ? (
                              <span style={{ fontWeight: 600, color: '#059669' }} title={`${anchoringFee.gt} GT, ${anchoringFee.days} gün, ${anchoringFee.isTurkish ? 'Türk' : 'Yabancı'} Bayrak`}>
                                ${anchoringFee.fee.toFixed(2)}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem' }}>{formatDuration(anchorage.hours_anchored)}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#6366f1' }}>
                              {calculateCalendarDays(anchorage.entry_date)}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <span className={`badge ${badge.class}`} style={{ fontSize: '0.8rem' }}>
                              <BadgeIcon size={12} style={{ marginRight: '0.25rem' }} />
                              {badge.text}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                              <button
                                className={`btn btn-sm ${isFamiliar ? 'btn-warning' : 'btn-outline-warning'}`}
                                onClick={() => toggleFamiliarShip(anchorage)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                title={isFamiliar ? 'Tanıdık listesinden çıkar' : 'Tanıdık listesine ekle'}
                              >
                                <Star size={14} fill={isFamiliar ? 'currentColor' : 'none'} />
                              </button>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleExitClick(anchorage)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                              >
                                <CheckCircle size={14} />
                                Çıkış
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {renderPagination(activeNormal.length, currentPageNormal, setCurrentPageNormal)}
            </div>
          </div>
        )}

        {/* Departed Section */}
        {departed.length > 0 && filterStatus !== 'anchored' && (
          <div className="card">
            <div className="card-header" style={{ padding: '0.75rem 1rem' }}>
              <h3 className="card-title" style={{ fontSize: '1.1rem', margin: 0 }}>
                Çıkış Yapan Gemiler ({departed.length})
              </h3>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-responsive">
                <table className="table table-sm" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr style={{ fontSize: '0.85rem' }}>
                      <th style={{ padding: '0.5rem', width: '40px' }}>
                        <input
                          type="checkbox"
                          onChange={() => handleSelectAll(paginatedDeparted)}
                          checked={paginatedDeparted.length > 0 && paginatedDeparted.every(anc => selectedIds.includes(anc.id))}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th style={{ padding: '0.5rem' }}>Gemi Adı</th>
                      <th style={{ padding: '0.5rem' }}>MMSI</th>
                      <th style={{ padding: '0.5rem' }}>Geliş Tarihi</th>
                      <th style={{ padding: '0.5rem' }}>Çıkış Tarihi</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Demir Ücreti</th>
                      <th style={{ padding: '0.5rem' }}>Toplam Süre</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Gün</th>
                      <th style={{ padding: '0.5rem' }}>Durum</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDeparted.map((anchorage) => {
                      const badge = getAlertBadge(anchorage.alert_level, anchorage.hours_anchored);
                      const BadgeIcon = badge.icon;
                      const isRegistered = isRegisteredVessel(anchorage.ship_name);
                      const isFamiliar = isFamiliarShip(anchorage.ship_name);
                      const anchoringFee = calculateAnchoringFee(anchorage.ship_name, anchorage.hours_anchored);
                      
                      return (
                        <tr 
                          key={anchorage.id} 
                          style={{ 
                            fontSize: '0.9rem',
                            backgroundColor: isRegistered ? '#e8f5e9' : isFamiliar ? '#fff3e0' : 'inherit'
                          }}
                        >
                          <td style={{ padding: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(anchorage.id)}
                              onChange={() => handleSelectOne(anchorage.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {anchorage.ship_name}
                              {isRegistered && (
                                <span className="badge badge-success" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }} title="Kayıtlı Gemi">
                                  <Ship size={10} /> Kayıtlı
                                </span>
                              )}
                              {isFamiliar && !isRegistered && (
                                <span className="badge" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', backgroundColor: '#ff9800', color: 'white' }} title="Tanıdık Armatör">
                                  <Star size={10} /> Tanıdık
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '0.5rem' }}>{anchorage.mmsi || '-'}</td>
                          <td style={{ padding: '0.5rem' }}>{new Date(anchorage.entry_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td style={{ padding: '0.5rem' }}>{new Date(anchorage.exit_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            {anchoringFee ? (
                              <span style={{ fontWeight: 600, color: '#059669' }} title={`${anchoringFee.gt} GT, ${anchoringFee.days} gün, ${anchoringFee.isTurkish ? 'Türk' : 'Yabancı'} Bayrak`}>
                                ${anchoringFee.fee.toFixed(2)}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem' }}>{formatDuration(anchorage.hours_anchored)}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#6366f1' }}>
                              {calculateCalendarDays(anchorage.entry_date, anchorage.exit_date)}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem' }}>
                            <span className="badge badge-secondary" style={{ fontSize: '0.8rem' }}>Çıkış Yaptı</span>
                            <span className={`badge ${badge.class}`} style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                              <BadgeIcon size={12} style={{ marginRight: '0.25rem' }} />
                              {badge.text}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleCancelExit(anchorage)}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                              title="Çıkışı iptal et ve gemiyi tekrar demirsahasına ekle"
                            >
                              <Undo2 size={14} style={{ marginRight: '0.25rem' }} />
                              Çıkışı İptal Et
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {renderPagination(departed.length, currentPageDeparted, setCurrentPageDeparted)}
            </div>
          </div>
        )}

        {filteredAnchorages.length === 0 && (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
              <Anchor size={48} style={{ color: '#ccc', marginBottom: '1rem' }} />
              <p style={{ color: '#666', fontSize: '1.1em' }}>
                {searchTerm ? 'Arama kriterlerine uygun kayıt bulunamadı' : 'Henüz demirsahası kaydı bulunmuyor'}
              </p>
              <p style={{ color: '#999' }}>
                Email senkronizasyonu ile yeni gemilerin geliş bilgilerini otomatik alabilirsiniz
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Silinmiş Kayıtlar Modalı */}
      {showDeletedModal && (
        <div className="modal-overlay" onClick={() => setShowDeletedModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', width: '90%' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Trash2 size={20} style={{ marginRight: '0.5rem' }} />
                Silinmiş Kayıtlar ({deletedAnchorages.length})
              </h2>
              <button className="modal-close" onClick={() => setShowDeletedModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
              {deletedAnchorages.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                  <Trash2 size={48} style={{ color: '#ccc', marginBottom: '1rem' }} />
                  <p>Silinmiş kayıt bulunmuyor</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm" style={{ marginBottom: 0 }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                      <tr style={{ fontSize: '0.85rem' }}>
                        <th style={{ padding: '0.75rem 0.5rem' }}>Gemi Adı</th>
                        <th style={{ padding: '0.75rem 0.5rem' }}>MMSI</th>
                        <th style={{ padding: '0.75rem 0.5rem' }}>Geliş Tarihi</th>
                        <th style={{ padding: '0.75rem 0.5rem' }}>Çıkış Tarihi</th>
                        <th style={{ padding: '0.75rem 0.5rem' }}>Silinme Tarihi</th>
                        <th style={{ padding: '0.75rem 0.5rem' }}>Durum</th>
                        <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedData(deletedAnchorages, currentPageDeleted).map((anchorage) => {
                        const isRegistered = isRegisteredVessel(anchorage.ship_name);
                        const isFamiliar = isFamiliarShip(anchorage.ship_name);
                        
                        return (
                          <tr 
                            key={anchorage.id} 
                            style={{ 
                              fontSize: '0.9rem',
                              backgroundColor: isRegistered ? '#e8f5e9' : isFamiliar ? '#fff3e0' : 'inherit',
                              opacity: 0.8
                            }}
                          >
                            <td style={{ padding: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {anchorage.ship_name}
                                {isRegistered && (
                                  <span className="badge badge-success" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }} title="Kayıtlı Gemi">
                                    <Ship size={10} /> Kayıtlı
                                  </span>
                                )}
                                {isFamiliar && !isRegistered && (
                                  <span className="badge" style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', backgroundColor: '#ff9800', color: 'white' }} title="Tanıdık Armatör">
                                    <Star size={10} /> Tanıdık
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '0.5rem' }}>{anchorage.mmsi || '-'}</td>
                            <td style={{ padding: '0.5rem' }}>
                              {new Date(anchorage.entry_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })}
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              {anchorage.exit_date ? new Date(anchorage.exit_date).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              {anchorage.deleted_at ? new Date(anchorage.deleted_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white', fontSize: '0.8rem' }}>
                                <XCircle size={12} style={{ marginRight: '0.25rem' }} />
                                Silinmiş
                              </span>
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleRestoreAnchorage(anchorage.id)}
                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                title="Kaydı geri yükle"
                              >
                                <RotateCcw size={14} style={{ marginRight: '0.25rem' }} />
                                Geri Yükle
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {deletedAnchorages.length > 0 && (
              <div className="modal-footer" style={{ justifyContent: 'center', borderTop: '1px solid #dee2e6' }}>
                {renderPagination(deletedAnchorages.length, currentPageDeleted, setCurrentPageDeleted)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Exit Modal */}
      {showExitModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Gemi Çıkışı Kaydet</h2>
              <button className="modal-close" onClick={() => setShowExitModal(false)}>×</button>
            </div>
            <form onSubmit={handleExitSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label><strong>Gemi Adı:</strong> {selectedAnchorage?.ship_name}</label>
                </div>
                <div className="form-group">
                  <label><strong>Geliş:</strong> {new Date(selectedAnchorage?.entry_date).toLocaleString('tr-TR')}</label>
                </div>
                <div className="form-group">
                  <label><strong>Toplam Süre:</strong> {formatDuration(selectedAnchorage?.hours_anchored)}</label>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="exit_date">Çıkış Tarihi *</label>
                    <input
                      type="date"
                      id="exit_date"
                      className="form-control"
                      value={exitFormData.exit_date}
                      onChange={(e) => setExitFormData({ ...exitFormData, exit_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="exit_time">Çıkış Saati *</label>
                    <input
                      type="time"
                      id="exit_time"
                      className="form-control"
                      value={exitFormData.exit_time}
                      onChange={(e) => setExitFormData({ ...exitFormData, exit_time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notlar</label>
                  <textarea
                    id="notes"
                    className="form-control"
                    rows="3"
                    value={exitFormData.notes}
                    onChange={(e) => setExitFormData({ ...exitFormData, notes: e.target.value })}
                    placeholder="Opsiyonel notlar..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowExitModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-success">
                  <CheckCircle size={18} />
                  Çıkışı Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .badge {
          display: inline-flex;
          align-items: center;
          padding: 0.35em 0.65em;
          font-size: 0.85em;
          font-weight: 600;
          border-radius: 0.25rem;
        }

        .badge-success {
          background-color: #28a745;
          color: white;
        }

        .badge-warning {
          background-color: #ffc107;
          color: #000;
        }

        .badge-danger {
          background-color: #dc3545;
          color: white;
        }

        .badge-primary {
          background-color: #007bff;
          color: white;
        }

        .badge-secondary {
          background-color: #6c757d;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default Anchorage;
